import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StatementRelativePeriods, StatementType } from "shared";
import { useParams } from "react-router";

import {
  setActivityPeriodEnd,
  setActivityPeriodStart,
  setCurrentCurrency,
  setStatementPeriod,
  setStatementType,
} from "../slices/contactPagePrefs";
import { RootState } from "../store";
import {
  useFetchContactApplicablePoliciesQuery,
  useFetchContactQuery,
  useFetchContactStatementQuery,
} from "../services/api/contactsApi/contact";

import { useRouteOrganisationId } from "./useRouteOrganisationId";

// ID override when getting this contact outside of the contact page
export function useContact(idOveride?: number | string) {
  const { contactId } = useParams<{ contactId?: string }>();
  const { organisationId } = useRouteOrganisationId();
  const dispatch = useDispatch();
  const { currentCurrency } = useSelector((s: RootState) => s.contactPagePrefs);

  const contactIdValue = useMemo(() => {
    if (idOveride) {
      return Number(idOveride);
    }

    return contactId ? Number(contactId) : undefined;
  }, [idOveride, contactId]);

  const contactData = useFetchContactQuery({
    contactId: contactIdValue,
    organisationId,
  }, {
    skip: !contactIdValue || !organisationId,
  });

  useEffect(() => {
    if (!contactData.data) return;

    if (!currentCurrency && contactData.data?.uniqueCurrencies.length > 0) {
      dispatch(setCurrentCurrency(contactData.data?.uniqueCurrencies[0]));
    }

    if (!contactData.data?.uniqueCurrencies.includes(currentCurrency)) {
      dispatch(setCurrentCurrency(contactData.data?.uniqueCurrencies[0]));
    }
  }, [currentCurrency, contactData.data?.uniqueCurrencies]);

  return contactData;
}

export function useContactStatement() {
  const { contactId, organisationId } = useParams<{ organisationId: string; contactId?: string }>();
  const { statementPeriod, currentCurrency, statementType, activityPeriodStart, activityPeriodEnd } = useSelector((s: RootState) => s.contactPagePrefs);
  const dispatch = useDispatch();

  const contactStatementApi = useFetchContactStatementQuery({
    contactId: Number(contactId),
    organisationId,
    statementPeriod,
    statementType,
    activityPeriodStart: activityPeriodStart || undefined,
    activityPeriodEnd: activityPeriodEnd || undefined,
  }, {
    skip: !contactId || !organisationId,
    refetchOnMountOrArgChange: true,
  });

  return {
    ...contactStatementApi,
    data: contactStatementApi?.data ? contactStatementApi.data.statement : undefined,
    statementPeriod,
    currentCurrency,
    statementType,
    activityPeriodStart,
    activityPeriodEnd,
    setCurrentCurrency: (currency: string) => dispatch(setCurrentCurrency(currency)),
    setStatementPeriod: (period: StatementRelativePeriods) => dispatch(setStatementPeriod(period)),
    setStatementType: (type: StatementType) => dispatch(setStatementType(type)),
    setActivityPeriodStart: (date: string | null) => dispatch(setActivityPeriodStart(date)),
    setActivityPeriodEnd: (date: string | null) => dispatch(setActivityPeriodEnd(date)),
  };
}

export function useContactApplicablePolicies() {
  const { contactId, organisationId } = useParams<{ organisationId: string; contactId?: string }>();

  const contactApplicablePoliciesApi = useFetchContactApplicablePoliciesQuery({
    contactId: Number(contactId),
    organisationId,
  }, {
    skip: !contactId || !organisationId,
  });

  return contactApplicablePoliciesApi;
}
